import { useState } from 'react';
import Icon from './Icon';
import Sheet from './Sheet';
import PhotoViewer from './PhotoViewer';
import { useAiConfirm } from './AiConfirm';
import { PRIMARY } from './CaptureSheets';
import { resizeImages } from '../lib/image';
import { MEAL_LABELS, MEAL_SHORT, MEAL_STYLE, MEAL_TYPES, clockTime, grams, kcal, mealTypeForNow } from '../lib/format';
import { today } from '../lib/dates';
import { proteinSummary } from '../lib/day';
import { useApp } from '../lib/AppContext';
import { useAnnouncementAnchor } from './Announcements';
import { useFeedback } from './FeedbackChat';
import IngredientBreakdown from './IngredientBreakdown';
import { SourceLine } from './DataSource';
import { hasSources } from '../lib/foodTable';

const STATUS = {
  analyzed: 'AI ước tính',
  manual: 'Tự nhập',
  pending: 'Đang phân tích…',
  failed: 'Chưa phân tích được',
};

function SlotTile({ type, photo }) {
  if (photo) return <img src={photo.thumb_url} alt="" loading="lazy" className="size-12 shrink-0 rounded-[14px] object-cover" />;
  return (
    <span className={`grid size-12 shrink-0 place-items-center rounded-[14px] ${MEAL_STYLE[type].tile}`} aria-hidden="true">
      <Icon name={MEAL_STYLE[type].icon} className="size-6" />
    </span>
  );
}

function AddButton({ type, filled, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label={`Thêm ${MEAL_LABELS[type].toLowerCase()}`}
      className={`grid size-11 shrink-0 place-items-center rounded-full ${filled ? 'bg-base-200' : 'border-2 border-primary text-brand-text'}`}>
      <Icon name="plus" className="size-5" />
    </button>
  );
}

const mealLine = (meal) => (meal.calories == null
  ? STATUS[meal.analysis_status]
  : `${kcal(meal.calories)} kcal · ${grams(meal.protein_g)} đạm`);

// Fixed slots Bữa sáng, Bữa trưa, Bữa tối, Ăn vặt, each with its own + button. A slot can hold several
// meals, listed in the order they were logged. `onTakeout(type)` adds "Đặt ngoài" to the slots not logged yet.
export default function MealList({ day, onAdd, onOpen, onTakeout }) {
  const { editable } = useApp();
  const takeoutAnchor = useAnnouncementAnchor('day_takeout');
  const firstEmpty = MEAL_TYPES.find((type) => !day.meals.some((m) => m.meal_type === type));
  const total = day.meals.reduce((sum, m) => sum + (m.calories || 0), 0);
  const protein = proteinSummary(day.totals, day.protein_goal);
  // The meal due now on today's page carries a nudge toward the protein goal.
  const hintSlot = day.date === today() && protein.missing > 0 ? mealTypeForNow() : null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-bold">Bữa ăn</h2>
        <span className="tabular text-sm text-muted">{kcal(total)} kcal</span>
      </div>
      <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
        {MEAL_TYPES.map((type) => {
          const meals = day.meals.filter((m) => m.meal_type === type);
          if (meals.length === 0) {
            return (
              <li key={type} className="flex items-center gap-3 py-3">
                <SlotTile type={type} />
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold">{MEAL_LABELS[type]}</div>
                  <div className="text-sm text-muted">
                    {hintSlot === type ? `Còn ${protein.missing} g đạm để đạt mục tiêu` : 'Chưa ghi'}
                  </div>
                </div>
                {editable && onTakeout && (
                  <button ref={type === firstEmpty ? takeoutAnchor : undefined} type="button" onClick={() => onTakeout(type)}
                    aria-label={`Đặt ngoài cho ${MEAL_LABELS[type].toLowerCase()}`}
                    className="btn h-11 shrink-0 rounded-full border-line bg-base-100 px-3 text-sm font-semibold">
                    <Icon name="bag" className="size-4" /> Đặt ngoài
                  </button>
                )}
                {editable && <AddButton type={type} onClick={() => onAdd(type)} />}
              </li>
            );
          }
          return meals.map((meal, i) => (
            <li key={meal.id} className="flex items-center gap-3 py-3">
              <button type="button" onClick={() => onOpen(meal)} className="flex min-w-0 flex-1 items-center gap-3 text-left"
                aria-label={`${MEAL_LABELS[type]} lúc ${clockTime(meal.created_at)}: ${meal.title}, ${mealLine(meal)}`}>
                <SlotTile type={type} photo={meal.photos[0]} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {MEAL_LABELS[type]} · {clockTime(meal.created_at)}
                  </span>
                  <span className="block truncate text-[15px] font-semibold">{meal.title}</span>
                  <span className={`tabular block text-sm ${meal.analysis_status === 'failed' ? 'text-error' : 'text-muted'}`}>{mealLine(meal)}</span>
                </span>
              </button>
              {editable && i === meals.length - 1 && <AddButton type={type} filled onClick={() => onAdd(type)} />}
            </li>
          ));
        })}
      </ul>
    </section>
  );
}

const NUMBER_FIELDS = [
  { key: 'calories', label: 'kcal' },
  { key: 'protein_g', label: 'Đạm (g)' },
  { key: 'carbs_g', label: 'Tinh bột (g)' },
  { key: 'fat_g', label: 'Chất béo (g)' },
];

function EditMealForm({ meal, onSubmit, onCancel }) {
  const [values, setValues] = useState(() => ({
    meal_type: meal.meal_type,
    description: meal.description || '',
    ...Object.fromEntries(NUMBER_FIELDS.map(({ key }) => [key, meal[key] ?? ''])),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Only send numbers that changed, so editing the text keeps the AI estimate.
      const changed = NUMBER_FIELDS.filter(({ key }) => String(values[key]) !== String(meal[key] ?? ''));
      await onSubmit({
        meal_type: values.meal_type,
        description: values.description,
        ...Object.fromEntries(changed.map(({ key }) => [key, values[key]])),
      });
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div role="radiogroup" aria-label="Bữa" className="grid grid-cols-4 gap-1 rounded-[16px] bg-base-200 p-1">
        {MEAL_TYPES.map((type) => (
          <button key={type} type="button" role="radio" aria-checked={values.meal_type === type}
            onClick={() => setValues((v) => ({ ...v, meal_type: type }))}
            className={`h-11 rounded-[12px] text-sm font-semibold ${values.meal_type === type ? 'bg-neutral text-neutral-content' : ''}`}>
            {MEAL_SHORT[type]}
          </button>
        ))}
      </div>
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold">Mô tả</span>
        <textarea className="textarea w-full rounded-[14px] border-line text-base" value={values.description} onChange={set('description')} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        {NUMBER_FIELDS.map(({ key, label }) => (
          <label key={key} className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">{label}</span>
            <input type="number" inputMode="decimal" min="0" step="any" className="input tabular h-11 w-full rounded-[14px] border-line text-base"
              value={values[key]} onChange={set(key)} />
          </label>
        ))}
      </div>
      <p className="text-xs text-muted">Nếu sửa số liệu, bữa ăn sẽ được đánh dấu là tự nhập.</p>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <button type="submit" className={PRIMARY} disabled={busy}>
        {busy && <span className="loading loading-spinner loading-sm" />} Lưu thay đổi
      </button>
      <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={onCancel}>Hủy</button>
    </form>
  );
}

function MealDetail({ meal, onAction, onClose }) {
  const { editable } = useApp();
  const { openFeedback } = useFeedback() || {};
  const ingredientsAnchor = useAnnouncementAnchor('meal_ingredients');
  const [confirmAi, confirmSheet] = useAiConfirm('meal_reanalysis', { title: 'Phân tích lại bằng AI?' });
  const [editing, setEditing] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const run = async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const addPhotos = (event) => {
    const files = Array.from(event.target.files);
    event.target.value = '';
    if (files.length === 0) return;
    run('Đang tải ảnh lên…', async () => {
      const form = new FormData();
      (await resizeImages(files)).forEach((file) => form.append('photos[]', file));
      await onAction('post', `/meals/${meal.id}/photos`, form);
    });
  };

  if (editing) {
    return (
      <EditMealForm meal={meal} onCancel={() => setEditing(false)}
        onSubmit={async (attrs) => { await onAction('patch', `/meals/${meal.id}`, attrs); setEditing(false); }} />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {confirmSheet}
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-semibold text-muted">{MEAL_LABELS[meal.meal_type]} · {clockTime(meal.created_at)}</span>
        <span className="rounded-full bg-base-200 px-3 py-1 text-xs font-semibold text-muted">
          {meal.analysis_status === 'analyzed' && hasSources(meal.items) ? 'AI nhận diện' : STATUS[meal.analysis_status]}
        </span>
      </div>

      <PhotoViewer photos={meal.photos} index={viewing} onChange={setViewing} onClose={() => setViewing(null)} />
      {meal.photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 pt-2">
          {meal.photos.map((photo, i) => (
            <div key={photo.id} className="relative shrink-0">
              <button type="button" aria-label={`Xem ảnh ${i + 1}`} onClick={() => setViewing(i)}>
                <img src={photo.thumb_url} alt={`Ảnh bữa ăn ${i + 1}`} className="size-28 rounded-2xl object-cover" />
              </button>
              {editable && (
                <button type="button" aria-label={`Xóa ảnh ${i + 1}`} disabled={!!busy}
                  className="absolute -right-2 -top-2 grid size-8 place-items-center rounded-full bg-neutral text-neutral-content"
                  onClick={() => window.confirm('Xóa ảnh này?') && run('Đang xóa ảnh…', () => onAction('del', `/meal_photos/${photo.id}`))}>
                  <Icon name="x" className="size-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <dl className="tabular grid grid-cols-4 gap-2 text-center">
        {NUMBER_FIELDS.map(({ key, label }) => (
          <div key={key} className="rounded-[14px] bg-base-200 px-1 py-2">
            <dd className="text-[17px] font-extrabold">{key === 'calories' ? kcal(meal[key]) : grams(meal[key])}</dd>
            <dt className="text-[11px] text-muted">{label.replace(' (g)', '')}</dt>
          </div>
        ))}
      </dl>
      {meal.analysis_status !== 'manual' && <SourceLine items={meal.items} className="-mt-2" />}

      {meal.description && <p className="whitespace-pre-line text-sm">{meal.description}</p>}
      {meal.analysis_status === 'failed' && (
        <p role="alert" className="rounded-[14px] bg-[#fdebe4] p-3 text-sm text-[#8e3414]">{meal.analysis_error || 'Phân tích không thành công.'}</p>
      )}
      {meal.ai_summary && <p className="text-sm text-muted">{meal.ai_summary}</p>}

      {/* Analysed with the food table: ingredients with their source; the owner can correct weights (no AI). Typed
          totals ("tự nhập") hide them (they no longer add up); meals from before the table show their list as it was. */}
      {hasSources(meal.items) && meal.analysis_status !== 'manual' && (
        <div ref={ingredientsAnchor}>
          <IngredientBreakdown items={meal.items}
            onGrams={editable ? (index, g) => run('Đang tính lại…', () => onAction('patch', `/meals/${meal.id}`, { items: [{ index, grams: g }] })) : null} />
        </div>
      )}
      {meal.items.length > 0 && !hasSources(meal.items) && (
        <ul className="divide-y divide-line rounded-[20px] border border-line px-4">
          {meal.items.map((item, i) => (
            <li key={i} className="flex justify-between gap-3 py-3 text-sm">
              <span>
                <span className="font-semibold">{item.name}</span>
                <span className="block text-muted">{item.portion}</span>
              </span>
              <span className="tabular shrink-0 text-right">
                {kcal(item.calories)} kcal
                <span className="block text-muted">{grams(item.protein_g)} đạm</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* AI tính sai món: the chat opens with this meal, its photo and the AI's result attached. */}
      {editable && openFeedback && meal.analysis_status === 'analyzed' && (
        <button type="button" className="min-h-11 self-start text-sm font-semibold text-brand-text underline"
          onClick={() => { onClose(); openFeedback({ mode: 'personal', category: 'ai_wrong', meal }); }}>
          AI tính sai? Báo cho đội ngũ
        </button>
      )}

      {busy && <p className="flex items-center gap-2 text-sm text-muted"><span className="loading loading-spinner loading-xs" /> {busy}</p>}
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}

      {editable && (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100" disabled={!!busy} onClick={() => setEditing(true)}>
            <Icon name="pencil" className="size-4" /> Sửa
          </button>
          <label className={`btn h-11 rounded-[14px] border-line bg-base-100 ${busy ? 'btn-disabled' : ''}`}>
            <Icon name="photo" className="size-4" /> Thêm ảnh
            <input type="file" accept="image/*" multiple className="sr-only" onChange={addPhotos} disabled={!!busy} />
          </label>
          <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100" disabled={!!busy}
            onClick={() => confirmAi(() => run('AI đang phân tích lại…', () => onAction('post', `/meals/${meal.id}/reanalyze`)))}>
            <Icon name="refresh" className="size-4" /> Phân tích lại
          </button>
          <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 text-error" disabled={!!busy}
            onClick={() => window.confirm('Xóa bữa ăn này?') && run('Đang xóa…', async () => {
              await onAction('del', `/meals/${meal.id}`);
              onClose();
            })}>
            <Icon name="trash" className="size-4" /> Xóa
          </button>
        </div>
      )}
    </div>
  );
}

// Opens a logged meal: its numbers, dishes and photos, and (for the owner) edit, re-analyse and delete.
export function MealDetailSheet({ meal, onAction, onClose }) {
  return (
    <Sheet open={!!meal} onClose={onClose} title={meal?.title || 'Bữa ăn'}>
      {meal && <MealDetail key={meal.id} meal={meal} onAction={onAction} onClose={onClose} />}
    </Sheet>
  );
}
